import {css, Typography, useTheme} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import * as React from 'react';
import {defineMessages, FormattedMessage, useIntl, WrappedComponentProps} from 'react-intl';

import {FixedPaginationButton} from './FixedPaginationButton';
import {PageInput} from './PageInput';
import {PageSizeInput} from './PageSizeInput';
import {ResultCount} from './ResultCount';
import LeftArrow from 'src/assets/icons/leftArrowIcon.svg';
import LeftEndArrow from 'src/assets/icons/leftEndArrowIcon.svg';
import RightArrow from 'src/assets/icons/rightArrowIcon.svg';
import RightEndArrow from 'src/assets/icons/rightEndArrowIcon.svg';
import {GetStylesFunction} from 'src/js/common/types';
import {TranslateUtils} from 'src/js/common/utils';

const I18N = defineMessages({
  FIRST_PAGE: {
    id: 'common.page.first',
    defaultMessage: 'First page',
    description: 'text for first page',
  },
  PREV_PAGE: {
    id: 'common.page.previous',
    defaultMessage: 'Previous page',
    description: 'text for previous page',
  },
  NEXT_PAGE: {
    id: 'common.page.next',
    defaultMessage: 'Next page',
    description: 'text for next page',
  },
  LAST_PAGE: {
    id: 'common.page.last',
    defaultMessage: 'Last page',
    description: 'text for last page',
  },
});

interface OwnProps {
  activePage: number;
  activeSize?: number;
  countInPage?: number;
  handlePageChange: (page: number) => void;
  handlePageSizeChange?: (size: number) => void;
  pages: number;
  pageSize?: number;
  resultCount?: number;
  showPageSize?: boolean;
  showResultCount?: boolean;
  showMoveToPage?: boolean;
  /** Accepted for prop-compatibility with FixedResultPaginationBig (the compact variant has no page-number
   * buttons, so it is ignored here). */
  maxPageClickers?: number;
}

type Props = OwnProps & WrappedComponentProps;

export const FixedResultPagination: React.FC<Props> = (props) => {
  const {
    activePage,
    activeSize,
    countInPage,
    handlePageChange,
    handlePageSizeChange,
    pages,
    pageSize,
    resultCount,
    showPageSize = false,
    showResultCount = false,
  } = props;
  const theme = useTheme();
  const styles = getStyles({theme, params: {showPageSize}});
  const intl = useIntl();
  return (
    <div data-name='paging-wrapper' css={styles.dlResultPagination}>
      {showPageSize || showResultCount ? (
        <span data-name='paging-size' css={styles.dlResultCount}>
          {showPageSize && activeSize && handlePageSizeChange ? (
            <PageSizeInput activeSize={activeSize} onChange={handlePageSizeChange} />
          ) : null}
          {showResultCount && resultCount && pageSize && countInPage ? (
            <ResultCount count={resultCount} pageSize={pageSize} countInPage={countInPage} activePage={activePage} />
          ) : null}
        </span>
      ) : null}
      <span data-name='paging-buttons' css={styles.dlResultPages}>
        <span css={styles.dlPaginationInputWrapper}>
          <Typography variant={'caption'} data-name='fixed-result-pagination--page-input'>
            <PageInput activePage={activePage} onChange={handlePageChange} pages={pages} showPageLabel />
          </Typography>
          <Typography data-name={'fixed-result-pagination--page-from'} css={styles.dlPageInfo} variant={'caption'}>
            <FormattedMessage
              id={'common.pageFrom'}
              defaultMessage={'of {pages}'}
              description={'label Page number'}
              values={{pages: <>{pages}</>}}
            />
          </Typography>
        </span>
        <FixedPaginationButton
          dataName='fixed-result-pagination--button-first-page'
          onClick={handlePageChange}
          disabled={activePage <= 1}
          pageTo={1}
          title={TranslateUtils.translate({...I18N.FIRST_PAGE}, intl)}
        >
          <SvgIcon component={LeftEndArrow} inheritViewBox css={styles.leftEndArrow} />
        </FixedPaginationButton>
        <FixedPaginationButton
          dataName='fixed-result-pagination--button-prev-page'
          onClick={handlePageChange}
          disabled={activePage <= 1}
          pageTo={activePage - 1}
          title={TranslateUtils.translate({...I18N.PREV_PAGE}, intl)}
        >
          <SvgIcon component={LeftArrow} inheritViewBox css={styles.leftArrow} />
        </FixedPaginationButton>
        <FixedPaginationButton
          dataName='fixed-result-pagination--button-next-page'
          onClick={handlePageChange}
          disabled={activePage >= pages}
          pageTo={activePage + 1}
          title={TranslateUtils.translate({...I18N.NEXT_PAGE}, intl)}
        >
          <SvgIcon component={RightArrow} inheritViewBox css={styles.rightArrow} />
        </FixedPaginationButton>
        <FixedPaginationButton
          dataName='fixed-result-pagination--button-last-page'
          onClick={handlePageChange}
          disabled={activePage >= pages}
          pageTo={pages}
          title={TranslateUtils.translate({...I18N.LAST_PAGE}, intl)}
        >
          <SvgIcon component={RightEndArrow} inheritViewBox css={styles.rightEndArrow} />
        </FixedPaginationButton>
      </span>
    </div>
  );
};

FixedResultPagination.displayName = 'ResultPagination';

const getStyles: GetStylesFunction = (options) => {
  const isPageSizeShown = options?.params?.showPageSize;
  return {
    dlPaginationInputWrapper: css({
      marginRight: `${isPageSizeShown ? options?.theme?.spacing(8) : options?.theme?.spacing(1)}`,
      display: 'flex',
      alignItems: 'center',
    }),
    rightEndArrow: css`
      height: 12px;
      width: 16px;
    `,
    rightArrow: css`
      height: 16px;
      width: 12px;
    `,
    leftEndArrow: css`
      height: 12px;
      width: 16px;
    `,
    leftArrow: css`
      height: 16px;
      width: 12px;
    `,
    dlResultPagination: css`
      display: flex;
      align-items: center;
      justify-content: ${isPageSizeShown ? 'space-between' : 'flex-end'};
      order: 1;
      position: relative;
      min-height: 32px;
    `,
    dlResultCount: css`
      margin-left: var(--dlMarginSmall);
      margin-right: ${isPageSizeShown ? 0 : '7px'};
    `,
    dlPageLink: css`
      padding: 0 5px;
      margin-right: 3px;

      &:hover,
      &:focus,
      &:active:focus,
      &:active:hover {
        z-index: 2;
        outline: none;
      }
    `,
    dlPageInfo: css`
      margin-right: 5px;
      white-space: nowrap;
    `,
    dlPageLinkDisabled: css`
      &,
      &:hover,
      &:focus,
      &:active:focus,
      &:active:hover {
        cursor: not-allowed;
      }
    `,
    dlResultPages: css`
      margin: 0 var(--dlMarginExtraSmall);
      right: 0;
      display: flex;
      align-items: center;

      :global ul.pagination {
        margin: 0;

        li {
          a,
          button {
            .dlPageLink();
            color: var(--dlButtonFont);
            background-color: var(--dlPageButtonInactiveBackgroundColor);
            border: 1px solid var(--dlPageButtonInactiveBackgroundColor);
            border-radius: 10px;

            &:hover,
            &:focus,
            &:active:focus,
            &:active:hover {
              color: var(--dlButtonFont);
              background-color: var(--dlHighlightedColor);
              border-color: var(--dlHighlightedColor);
            }
          }
        }

        :global li.active {
          a,
          button {
            color: var(--dlButtonFont);
            background-color: var(--dlPageButtonBackgroundColor);
            border-color: var(--dlPageButtonBackgroundColor);
          }
        }

        :global li.disabled {
          a,
          button {
            .dlPageLinkDisabled();

            &,
            &:hover,
            &:focus,
            &:active:focus,
            &:active:hover {
              background-color: var(--dlContentBackgroundColor);
              border-color: var(--dlContentBackgroundColor);
            }
          }
        }
      }
    `,
  };
};
